// resources/js/admin/components/shared/pages/PageForm.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Plus } from "lucide-react";
import { Page, PageFormData } from "../../../types/features/Page";
import useLocales from "../../../hooks/features/locales/useLocales";
import useToken from "../../../hooks/features/auth/useToken";
import MediaAttachment from "./MediaAttachment";
import usePageMedia from "../../../hooks/features/media/useMediaPage";

interface PageFormProps {
    initialData?: Page;
    siteSlug: string;
    isEdit?: boolean;
    onSubmit: (data: PageFormData) => Promise<Page | null>;
    isLoading?: boolean;
    isSuccess?: boolean;
}

interface LocaleContent {
    locale: string;
    content: string;
    isValid: boolean;
}

export default function PageForm({
    initialData,
    siteSlug,
    isEdit = false,
    onSubmit,
    isLoading = false,
    isSuccess = false,
}: PageFormProps) {
    const navigate = useNavigate();
    const { token } = useToken();
    const { locales } = useLocales();

        const {media} = usePageMedia(initialData?.id || "")
       console.log("media" , media)

    const [formData, setFormData] = useState<PageFormData>({
        site_slug: siteSlug,
        slug_key: "",
        name: "",
        content: {},
        settings: {},
        status: "draft",
        change_summary: "",
    });

    // États pour les langues
    const [localeContents, setLocaleContents] = useState<LocaleContent[]>(() => {
        return [{ locale: "fr", content: "{}", isValid: true }];
    });
    const [showNewLocaleForm, setShowNewLocaleForm] = useState(false);
    const [newLocale, setNewLocale] = useState("");
    const [newLocaleContent, setNewLocaleContent] = useState("");
    const [newLocaleValid, setNewLocaleValid] = useState(true);

    // États pour les médias
    const [attachedMedia, setAttachedMedia] = useState<Array<{ id: string; field: string; url: string }>>([]);

    const activeLocales = locales?.filter(l => l.is_active) || [];

    useEffect(() => {
        if (initialData) {
            const contentObj = typeof initialData.content === 'object' && !Array.isArray(initialData.content)
                ? initialData.content
                : {};

            // Transformer le tableau [{key, label, value}] en objet {key: value} pour l'affichage
            const contents: LocaleContent[] = Object.keys(contentObj).length > 0
                ? Object.keys(contentObj).map(locale => {
                    const array = contentObj[locale];
                    const obj: Record<string, any> = {};
                    if (Array.isArray(array)) {
                        array.forEach((item: { key: string; label: string; value: any }) => {
                            obj[item.key] = item.value;
                        });
                    }
                    return {
                        locale,
                        content: JSON.stringify(obj, null, 2),
                        isValid: true,
                    };
                })
                : [{ locale: "fr", content: "{}", isValid: true }];

            setLocaleContents(contents);

            setFormData({
                site_slug: siteSlug,
                slug_key: initialData.slug_key,
                name: initialData.name || "",
                content: contentObj,
                settings: initialData.settings || {},
                status: initialData.status || "draft",
                change_summary: "",
            });
        }
    }, [initialData, siteSlug]);

    useEffect(() => {
        if (isSuccess) {
            setFormData({
                site_slug: siteSlug,
                slug_key: "",
                name: "",
                content: {},
                settings: {},
                status: "draft",
                change_summary: "",
            });
            setLocaleContents([{ locale: "fr", content: "{}", isValid: true }]);
            setShowNewLocaleForm(false);
            setNewLocale("");
            setNewLocaleContent("");
            setAttachedMedia([]);
        }
    }, [isSuccess, siteSlug]);

    // Gestion des langues
    const handleAddLocale = () => {
        if (!newLocale) {
            alert("Veuillez sélectionner une langue");
            return;
        }

        if (localeContents.some(lc => lc.locale === newLocale)) {
            alert("Cette langue existe déjà");
            return;
        }

        try {
            JSON.parse(newLocaleContent);
        } catch {
            alert("Le JSON est invalide");
            setNewLocaleValid(false);
            return;
        }

        // Transformer l'objet {key: value} en tableau [{key, label, value}] pour la sauvegarde
        const parsed = JSON.parse(newLocaleContent);
        const array = Object.entries(parsed).map(([key, value]) => ({
            key,
            label: key,
            value,
        }));

        const newContent = { ...formData.content, [newLocale]: array };
        setFormData(prev => ({ ...prev, content: newContent }));

        setLocaleContents(prev => [...prev, {
            locale: newLocale,
            content: newLocaleContent,
            isValid: true,
        }]);

        setShowNewLocaleForm(false);
        setNewLocale("");
        setNewLocaleContent("");
        setNewLocaleValid(true);
    };

    const handleRemoveLocale = (locale: string) => {
        if (localeContents.length <= 1) {
            alert("Vous devez avoir au moins une langue");
            return;
        }

        const newContents = localeContents.filter(lc => lc.locale !== locale);
        setLocaleContents(newContents);

        const newFormContent = { ...formData.content };
        delete newFormContent[locale];
        setFormData(prev => ({ ...prev, content: newFormContent }));
    };

    const handleContentChange = (locale: string, value: string) => {
        setLocaleContents(prev => prev.map(lc =>
            lc.locale === locale ? { ...lc, content: value, isValid: true } : lc
        ));

        try {
            const parsed = JSON.parse(value);
            // Transformer l'objet {key: value} en tableau [{key, label, value}] pour la sauvegarde
            const array = Object.entries(parsed).map(([key, val]) => ({
                key,
                label: key,
                value: val,
            }));

            const newContent = { ...formData.content, [locale]: array };
            setFormData(prev => ({ ...prev, content: newContent }));
            setLocaleContents(prev => prev.map(lc =>
                lc.locale === locale ? { ...lc, isValid: true } : lc
            ));
        } catch {
            setLocaleContents(prev => prev.map(lc =>
                lc.locale === locale ? { ...lc, isValid: false } : lc
            ));
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleMediaChange = (media: Array<{ id: string; field: string; url: string }>) => {
        setAttachedMedia(media);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Vérifier que toutes les langues ont un JSON valide
        for (const lc of localeContents) {
            if (!lc.isValid) {
                alert(`Le JSON pour la langue ${lc.locale} est invalide`);
                return;
            }
        }

        try {
            const page = await onSubmit(formData);

            if (!page) {
                alert("Erreur lors de la création de la page");
                return;
            }

            if (attachedMedia.length > 0) {
                const { pageMediaService } = await import("../../../services/pageMediaService");
                for (const media of attachedMedia) {
                    if (media.id) {
                        await pageMediaService.attach(token, page.id, {
                            media_id: media.id,
                            field: media.field,
                            position: 0,
                            settings: {},
                        });
                    }
                }
            }

            alert("Page enregistrée avec succès");
            navigate("/admin/pages");
        } catch (error) {
            console.error("Erreur lors de l'enregistrement", error);
            alert("Erreur lors de l'enregistrement de la page");
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {/* Informations générales */}
                <div className="mb-6">
                    <div className="border-b border-gray-200 mb-4 pb-2">
                        <h2 className="text-lg font-semibold text-gray-900">Informations générales</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Slug <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="slug_key"
                                value={formData.slug_key}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                placeholder="accueil"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nom
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                placeholder="Page d'accueil"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Statut
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            >
                                <option value="draft">Brouillon</option>
                                <option value="review">En relecture</option>
                                <option value="published">Publié</option>
                                <option value="archived">Archivé</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Résumé des modifications
                            </label>
                            <input
                                type="text"
                                name="change_summary"
                                value={formData.change_summary}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                placeholder="Création de la page"
                            />
                        </div>
                    </div>
                </div>

                {/* Section Langues */}
                <div className="mb-6 border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Contenu</h3>
                        <button
                            type="button"
                            onClick={() => setShowNewLocaleForm(!showNewLocaleForm)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
                        >
                            <Plus size={16} />
                            Ajouter du contenu pour une autre langue
                        </button>
                    </div>

                    {/* Formulaire pour ajouter une nouvelle langue */}
                    {showNewLocaleForm && (
                        <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Ajouter une langue</h4>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Langue
                                    </label>
                                    <select
                                        value={newLocale}
                                        onChange={(e) => setNewLocale(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                                    >
                                        <option value="">Sélectionner une langue</option>
                                        {activeLocales
                                            .filter(l => !localeContents.some(lc => lc.locale === l.code))
                                            .map((locale) => (
                                                <option key={locale.code} value={locale.code}>
                                                    {locale.flag} {locale.name} ({locale.code.toUpperCase()})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contenu (JSON)
                                    </label>
                                    <textarea
                                        value={newLocaleContent}
                                        onChange={(e) => {
                                            setNewLocaleContent(e.target.value);
                                            try {
                                                JSON.parse(e.target.value);
                                                setNewLocaleValid(true);
                                            } catch {
                                                setNewLocaleValid(false);
                                            }
                                        }}
                                        rows={6}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/50 font-mono text-sm ${
                                            newLocaleValid ? 'border-gray-300' : 'border-red-500 bg-red-50'
                                        }`}
                                        placeholder='{"hero_title": "Titre", "hero_subtitle": "Sous-titre"}'
                                    />
                                    {!newLocaleValid && (
                                        <p className="text-red-500 text-sm mt-1">⚠️ JSON invalide</p>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleAddLocale}
                                        disabled={!newLocale || !newLocaleValid}
                                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                                    >
                                        Ajouter
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowNewLocaleForm(false);
                                            setNewLocale("");
                                            setNewLocaleContent("");
                                            setNewLocaleValid(true);
                                        }}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Liste des langues et leurs contenus */}
                    {localeContents.map((lc) => {
                        const localeInfo = activeLocales.find(l => l.code === lc.locale);
                        return (
                            <div key={lc.locale} className="mb-4 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">
                                            {localeInfo?.flag} {localeInfo?.name || lc.locale.toUpperCase()}
                                        </span>
                                        {!lc.isValid && (
                                            <span className="text-red-500 text-sm">⚠️ JSON invalide</span>
                                        )}
                                    </div>
                                    {localeContents.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveLocale(lc.locale)}
                                            className="text-red-500 hover:text-red-700 text-sm"
                                        >
                                            Supprimer
                                        </button>
                                    )}
                                </div>
                                <textarea
                                    value={lc.content}
                                    onChange={(e) => handleContentChange(lc.locale, e.target.value)}
                                    rows={6}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/50 font-mono text-sm ${
                                        lc.isValid ? 'border-gray-300' : 'border-red-500 bg-red-50'
                                    }`}
                                    placeholder='{"hero_title": "Titre", "hero_subtitle": "Sous-titre"}'
                                />
                            </div>
                        );
                    })}
                </div>

                {/* Section Médias - Composant MediaAttachment */}
                <MediaAttachment
                    siteSlug={siteSlug}
                    initialMedia={initialData?.media}
                    pageId={initialData?.id}
                    onMediaChange={handleMediaChange}
                />
            </div>

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    Annuler
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                    <Save className="w-4 h-4" />
                    {isLoading ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
                </button>
            </div>
        </form>
    );
}